import { useCallback, useMemo } from 'react'
import { PositionMeshConnector, useModelClickListenerV2 } from '@scene'
import { usePositionVisibilityLookup } from '@editor/hooks'
import { ThreeEvent } from '@react-three/fiber'
import { useControlStore, useElementSelectionStore, useModelStore } from '@editorStores'
import { useFilterState } from 'src/components/pages/Editor/pages/StructuralPlanning/hooks/useFilterState'

interface Props {
  isVisibleOverwrite?: (guid: string) => boolean
  onClick?: (guid: string, event: ThreeEvent<MouseEvent>) => void | { selectedGuids?: string[] }
  selectedElements?: string[]
}

export const VisiblePositions = ({ isVisibleOverwrite, onClick, selectedElements = [] }: Props) => {
  const { rips, lintels } = useModelStore(state => state.model)
  const { showRips, showLintels } = useFilterState()
  const positionVisibilityLookup = usePositionVisibilityLookup()
  const actionMode = useControlStore(state => state.actionMode)

  const positionGuids = useMemo(
    () => [
      ...(rips?.map(rip => rip.position_guid) || []),
      ...(lintels?.map(lintel => lintel.position_guid) || []),
    ],
    [rips, lintels],
  )

  const highlightedSecondaryIds = useElementSelectionStore(state => state.highlightedSecondaryIds)

  const onClickPosition = useCallback(
    (guid: string, event: ThreeEvent<MouseEvent>) => onClick?.(guid, event),
    [onClick],
  )

  useModelClickListenerV2('position', onClickPosition, {
    enabled: actionMode !== 'hide',
    guids: positionGuids,
  })

  return (
    <>
      {rips && (
        <>
          {rips.map(rip => {
            const isVisible =
              showRips &&
              (isVisibleOverwrite
                ? isVisibleOverwrite(rip.position_guid)
                : positionVisibilityLookup[rip.position_guid])

            const isSelected = selectedElements.includes(rip.position_guid)

            // TODO: <simple cylinder mesh> will be missing
            return (
              <PositionMeshConnector
                key={rip.position_guid}
                guid={rip.position_guid}
                visible={isVisible}
                selected={isSelected}
                target={highlightedSecondaryIds.has(rip.position_guid)}
              />
            )
          })}
        </>
      )}

      {lintels && (
        <>
          {lintels.map(lintel => {
            const isVisible =
              showLintels &&
              (isVisibleOverwrite
                ? isVisibleOverwrite(lintel.position_guid)
                : positionVisibilityLookup[lintel.position_guid])

            const isSelected = selectedElements.includes(lintel.position_guid)
            return (
              <PositionMeshConnector
                key={lintel.position_guid}
                guid={lintel.position_guid}
                visible={isVisible}
                selected={isSelected}
                target={highlightedSecondaryIds.has(lintel.position_guid)}
              />
            )
          })}
        </>
      )}
    </>
  )
}
